import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import * as yup from "yup";
import { DashboardLayout } from "component/layout/dashboard";
import { FeatherIcon } from "component/shared/feather-icon";
import { FormField } from "component/shared/form-fields";
import { Switch } from "component/shared/switch";
import { SettingsSection } from "component/shared/settings-page";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { RecipientMultiSelect, RecipientOption } from "component/shared/recipient-multi-select";
import { BrandCheckbox } from "component/shared/brand-checkbox";
import { PageProps } from "shared/page-props";
import { User } from "model/user";
import { validate } from "shared/yup";
import { RootState } from "redux/store";
import { saveUser } from "redux/slice/authentication";
import { styles } from "./refreshed-style";

const REPLY_TO_TOOLTIP =
	"Used as the reply-to address on emails sent to leads. For single agents this is the default; " +
	"for teams, this is used when a lead has no assigned agent.";

const ACCOUNT_OWNER = "accountOwner";
const ASSIGNED_AGENT = "assignedAgent";

/**
 * The emails a client can be copied on. Four items, not the five in the original design:
 * Angelica found that "Saved search welcome" and "Property organizer welcome" are the same
 * email — EmailService.sendSignUpConfirmationToUser always fires PROPERTY_ORGANIZER_CONFIRMATION
 * regardless of whether a search was involved — so the duplicate was dropped (2026-09-01).
 */
const COPY_TYPES = [
	{ key: "savedSearchAlerts", label: "Saved search alerts" },
	{ key: "propertyOrganizerWelcome", label: "Property organizer welcome" },
	{ key: "leadSharesSavedProperty", label: "Lead shares saved property" },
	{ key: "registeredLeadWelcome", label: "Registered lead welcome" },
];

const mapStateToProps = (state: RootState) => ({
	authLoading: state.authentication.loading,
	authError: state.authentication.error,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({ saveUser }, dispatch);

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User;
}

interface EditableFields {
	dailyEmail: string;
	emailUpdatesForSoldListings: boolean;
	sendEmailUpdatesForMlsChanges: boolean;
}

interface State extends EditableFields {
	copiesEnabled: boolean;
	copyRecipients: string[];
	copyTypes: string[];
	showValidation: boolean;
	saving: boolean;
	snackbarOpen: boolean;
}

function fieldsFromUser(user: User): EditableFields {
	return {
		dailyEmail: user.dailyEmail ?? "",
		// Both default on when the client has never set them.
		emailUpdatesForSoldListings: user.emailUpdatesForSoldListings !== false,
		sendEmailUpdatesForMlsChanges: user.sendEmailUpdatesForMlsChanges !== false,
	};
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			...fieldsFromUser(props.user),
			copiesEnabled: false,
			copyRecipients: [ACCOUNT_OWNER, ASSIGNED_AGENT],
			copyTypes: COPY_TYPES.map((type) => type.key),
			showValidation: false,
			saving: false,
			snackbarOpen: false,
		};
	}

	public componentDidUpdate(prevProps: Props) {
		const { authLoading, authError } = this.props;
		if (prevProps.authLoading && !authLoading && !authError && this.state.saving) {
			this.setState({ saving: false });
		}
	}

	private validationSchema = {
		dailyEmail: yup.string().email("Invalid Email").required("This field is required"),
	};

	private isDirty(): boolean {
		const original = fieldsFromUser(this.props.user);
		return (Object.keys(original) as Array<keyof EditableFields>)
			.some((key) => original[key] !== this.state[key]);
	}

	private handleSave = () => {
		const { saveUser, user } = this.props;
		const errors = validate(this.validationSchema, this.state);
		if (errors) {
			this.setState({ showValidation: true });
			return;
		}
		const { dailyEmail, emailUpdatesForSoldListings, sendEmailUpdatesForMlsChanges } = this.state;
		saveUser({
			user: { ...user, dailyEmail, emailUpdatesForSoldListings, sendEmailUpdatesForMlsChanges },
		});
		this.setState({ saving: true, snackbarOpen: true, showValidation: false });
	};

	private handleCancel = () => {
		this.setState({ ...fieldsFromUser(this.props.user), showValidation: false });
	};

	private copyRecipientOptions(): RecipientOption[] {
		const { user } = this.props;
		return [
			{
				id: ACCOUNT_OWNER,
				label: "Account Owner",
				detail: user.notificationEmail || user.companyEmail,
			},
			{ id: ASSIGNED_AGENT, label: "Assigned Agent" },
		];
	}

	private renderReplyTo() {
		const { showValidation } = this.state;
		const errors = validate(this.validationSchema, this.state);
		return (
			<SettingsSection
				title="Reply-to Email"
				description="Set the email address leads will reach when they reply to emails sent from your account. For teams, the assigned agent's email is used when available."
				fieldColumn
			>
				<FormField
					label="Reply-to Email"
					placeholder="you@example.com"
					tooltip={REPLY_TO_TOOLTIP}
					value={this.state.dailyEmail}
					onChange={(event) => this.setState({ dailyEmail: event.target.value })}
					error={showValidation && !!errors?.dailyEmail}
					helperText={showValidation ? errors?.dailyEmail : undefined}
				/>
			</SettingsSection>
		);
	}

	private renderCheckbox(
		checked: boolean,
		onChange: (value: boolean) => void,
		title: string,
		description: string,
	) {
		const { classes = {} } = this.props;
		return (
			<Mui.FormControlLabel
				control={
					<BrandCheckbox
						checked={checked}
						onChange={(event) => onChange(event.target.checked)}
					/>
				}
				label={
					<Mui.Box>
						<Mui.Typography className={classes.checkboxTitle}>{title}</Mui.Typography>
						<Mui.Typography className={classes.checkboxDescription}>{description}</Mui.Typography>
					</Mui.Box>
				}
				className={classes.checkboxOption}
			/>
		);
	}

	private renderListingAlerts() {
		const { classes = {} } = this.props;
		const { emailUpdatesForSoldListings, sendEmailUpdatesForMlsChanges } = this.state;
		return (
			<SettingsSection
				title="Listing Alerts"
				description="Choose what to include in listing alert and saved search emails sent to leads:"
			>
				<Mui.Box className={classes.stackedOptions}>
					{this.renderCheckbox(
						emailUpdatesForSoldListings,
						(value) => this.setState({ emailUpdatesForSoldListings: value }),
						"Sold listings",
						"When enabled, sold listings will be included in listing alert emails. Subject to MLS data availability.",
					)}
					{this.renderCheckbox(
						sendEmailUpdatesForMlsChanges,
						(value) => this.setState({ sendEmailUpdatesForMlsChanges: value }),
						"Listing price changes",
						"When enabled, listing price changes will be included in listing alert emails.",
					)}
				</Mui.Box>
			</SettingsSection>
		);
	}

	private renderEmailCopies() {
		const { classes = {} } = this.props;
		const { copiesEnabled, copyRecipients, copyTypes } = this.state;
		return (
			<SettingsSection title="Email Copies">
				<Switch
					checked={copiesEnabled}
					onChange={(value) => this.setState({ copiesEnabled: value })}
					label="Send copies of lead emails"
					description="When enabled, copies of emails sent to leads will be delivered to your primary contact, the lead's assigned agent, or any other email address you specify."
				/>
				{copiesEnabled && (
					<Mui.Box className={classes.disclosed}>
						<Mui.Typography className={classes.copiesLabel}>Send copies to:</Mui.Typography>
						<Mui.Box className={classes.fieldColumn}>
							<Mui.Typography className={classes.readOnlyLabel}>Email Recipients</Mui.Typography>
							<RecipientMultiSelect
								options={this.copyRecipientOptions()}
								selectedIds={copyRecipients}
								onChange={(ids) => this.setState({ copyRecipients: ids })}
							/>
						</Mui.Box>
						<Mui.Divider className={classes.divider} />
						<Mui.Typography className={classes.copiesLabel}>
							Select which emails you'd like to receive copies of:
						</Mui.Typography>
						{COPY_TYPES.map((type) => (
							<Mui.Box key={type.key}>
								<Mui.FormControlLabel
									control={
										<BrandCheckbox
											checked={copyTypes.includes(type.key)}
											onChange={(event) =>
												this.setState({
													copyTypes: event.target.checked
														? [...copyTypes, type.key]
														: copyTypes.filter((key) => key !== type.key),
												})
											}
										/>
									}
									label={type.label}
								/>
							</Mui.Box>
						))}
					</Mui.Box>
				)}
			</SettingsSection>
		);
	}

	public render() {
		const { user, authError, authLoading, classes = {} } = this.props;
		const { saving, snackbarOpen } = this.state;
		const dirty = this.isDirty();
		return (
			<DashboardLayout
				permitted={user.admin || (user.type.agent && user.permissions.modifySubscriptions)}
				title="Emails"
				header={
					<Mui.Typography variant="h1">
						<FeatherIcon>
							<Icons.Mail />
						</FeatherIcon>
						Emails
					</Mui.Typography>
				}
			>
				<Mui.Typography variant="body2" className={classes.pageSubtitle}>
					Manage your primary contact information and configure which email notifications your
					account receives.
				</Mui.Typography>

				{this.renderReplyTo()}
				{this.renderListingAlerts()}
				{this.renderEmailCopies()}

				<SettingsStickyFooter
					dirty={dirty}
					saving={!!(saving && authLoading)}
					authError={authError ?? null}
					onSave={this.handleSave}
					onCancel={this.handleCancel}
				/>

				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />

				<Mui.Snackbar
					open={snackbarOpen && !authLoading && !authError}
					message="Email settings saved"
					autoHideDuration={6000}
					onClose={() => this.setState({ snackbarOpen: false })}
				/>
			</DashboardLayout>
		);
	}

}

export const RefreshedClientEmailSetup = withStyles(
	connect(mapStateToProps, mapDispatchToProps)(Component),
	styles,
);
